import { parse } from "querystring"

import { useEffect, useMemo, useState } from "react"
import Cookie from "js-cookie"
import { addDays } from "date-fns"
import { CSSTransition } from "react-transition-group"
import { useToggle } from "react-use"

import { useClientAccountContext } from "modules/ClientAccount/Context"
import { Analytics } from "utils/analytics"
import {
    useGetClientCalendarLink,
    useGetPastBookingsWithPagination,
    useHasFutureFixedTime,
} from "modules/ClientAccount/hooks"
import { isCancelled, randomize } from "utils/helpers"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { AdviceBlock, AdviceBlockDiscount } from "components"
import { UserInfoPageHash } from "modules/ClientAccount/index"
import { useBannerSettings } from "utils/Banners/useBannerSettings"

const COOKIE_NAME = "alter-client-advice-widget"

export function AdviceBlockCalendar() {
    function handleOnClick() {
        Analytics.calendar_add_lk()
    }

    const content = {
        title: <>Добавьте сессии в&nbsp;календарь</>,
        description: (
            <>
                Добавьте запланированные сессии в&nbsp;свой календарь, чтобы
                не&nbsp;пропустить&nbsp;их.
            </>
        ),
        button: (
            <AdviceBlock.ButtonUrl text="Добавить" onClick={handleOnClick} hash="attach-calendar" />
        ),
        className: "is-client",
    }

    return <AdviceBlock {...content} />
}

export function AdviceBlockFixTime() {
    const [show, toggleShow] = useToggle(true)
    const [, { toggleShowAutoAdviceBlock }] = useClientAccountContext()

    const onExited = () => {
        toggleShowAutoAdviceBlock?.()
        Cookie.set(COOKIE_NAME, "1", { expires: addDays(new Date(), 3) })
    }

    const content = {
        title: "Закрепите время сессий",
        description: (
            <>
                Попросите психолога закрепить за&nbsp;вами время&nbsp;&mdash; так вы&nbsp;сможете
                заниматься регулярно и&nbsp;никто не&nbsp;займет ваше время. Новые сессии будут
                назначаться автоматически, вам останется только их&nbsp;оплатить.
            </>
        ),
        button: <AdviceBlock.Button text="Хорошо" onClick={toggleShow} />,
        className: "is-client",
    }

    return (
        <CSSTransition in={show} timeout={200} classNames="fade" unmountOnExit onExited={onExited}>
            <AdviceBlock {...content} />
        </CSSTransition>
    )
}

export function AdviceBlockTelegramAccount() {
    const content = {
        title: <>Подключите уведомления в&nbsp;Telegram</>,
        description: <>Получайте сообщения о&nbsp;сессиях удобным способом.</>,
        button: (
            <AdviceBlock.ButtonUrl text="Подключить" hash={UserInfoPageHash.TELEGRAM_ACCOUNT} />
        ),
        className: "is-client",
    }

    return <AdviceBlock {...content} />
}

export function RandomAdviceBlock() {
    const [widgetId, setWidgetId] = useState<string>()
    const [{ showAutoAdviceBlock, showRefWidget }] = useClientAccountContext()

    const { data: hasFeatureFixedTime, isLoading: hasFeatureFixedTimeLoading } =
        useHasFutureFixedTime()
    const { pastBookingFirstPage, isLoading: pastBookingLoading } =
        useGetPastBookingsWithPagination()

    const { data: calendarLink, isLoading: calendarLinkLoading } = useGetClientCalendarLink()
    const isShowCalendarAdvice = !calendarLink?.share_link

    const [{ userAccount }] = useClientContext()
    const { isShowTelegramBanner } = useBannerSettings()

    const showAdviceBlockIfNotExpired = !Cookie.get(COOKIE_NAME)

    const widgets = useMemo(() => {
        const data = []
        const hasNotCanceledBookings = pastBookingFirstPage?.some(
            (item) => !isCancelled(item.status)
        )
        const isShowFixTime =
            !hasFeatureFixedTime &&
            showAutoAdviceBlock &&
            hasNotCanceledBookings &&
            showAdviceBlockIfNotExpired
        const isShowDiscount = showRefWidget && userAccount.allowed_referral_program

        if (isShowFixTime && (!widgetId || widgetId === "fix-time")) {
            data.push(<AdviceBlockFixTime />)
        }

        if (isShowCalendarAdvice && (!widgetId || widgetId === "calendar")) {
            data.push(<AdviceBlockCalendar />)
        }

        if (isShowDiscount && (!widgetId || widgetId === "discount")) {
            data.push(<AdviceBlockDiscount className="is-client" />)
        }

        if (
            isShowTelegramBanner &&
            !userAccount.telegram_account &&
            (!widgetId || widgetId === "telegram")
        ) {
            data.push(<AdviceBlockTelegramAccount />)
        }

        return data
    }, [
        hasFeatureFixedTime,
        pastBookingFirstPage,
        showAutoAdviceBlock,
        calendarLink,
        isShowCalendarAdvice,
        hasFeatureFixedTimeLoading,
        calendarLinkLoading,
        pastBookingLoading,
        userAccount,
        isShowTelegramBanner,
        widgetId,
    ])

    const randomWidget = useMemo(() => randomize(widgets), [widgets])

    useEffect(() => {
        function onHashChange() {
            const hashQueryString = parse(document.location.hash.slice(1))
            setWidgetId(hashQueryString["advice"]?.toString())
        }
        window.addEventListener("hashchange", onHashChange)
        onHashChange()
        return () => {
            window.removeEventListener("hashchange", onHashChange)
        }
    }, [])

    if (hasFeatureFixedTimeLoading || calendarLinkLoading || pastBookingLoading) {
        return null
    }

    return <>{randomWidget}</>
}
